import { buildPositionRows } from '@trading/positions-ui';
import type { PositionRow, EnrichmentInput, RawBar, RawPositionInput } from '@trading/positions-ui';
import { getPositions as alpacaGetPositions, getBars as alpacaGetBars } from './alpaca.js';
import {
    getOrders as ordersApiGetOrders,
    getUpsideByRunIds as ordersApiGetUpsideByRunIds,
    getDevicePositions as ordersApiGetDevicePositions,
} from './orders-api.js';
import type { OrderDoc, DevicePosition } from './orders-api.js';
import type { AppConfig } from './config.js';

export interface ComposePositionRowsResult {
    rows: PositionRow[];
    errors: string[];
}

export interface PositionsAdapterDeps {
    getPositions: typeof alpacaGetPositions;
    /** Preferred source of enrichment — see `buildRowsFromDevicePositions`. */
    getDevicePositions: typeof ordersApiGetDevicePositions;
    // ── used only on the fallback path, when the device-positions call fails ──────────────
    getOrders: typeof ordersApiGetOrders;
    getUpsideByRunIds: typeof ordersApiGetUpsideByRunIds;
    getBars: typeof alpacaGetBars;
}

/**
 * The position-app adapter onto the shared positions domain (see the `shared-positions-view`
 * skill): reaches trading_server's device-API-key-gated `GET /api/device/positions` for
 * enrichment (it already carries ATR, stop-loss, target, days-to-target and the flags, in one
 * call), with Alpaca REST as the source of the live position list and prices. If the device
 * call fails, falls back to the pre-device-endpoint path — Alpaca daily bars for client-side
 * ATR plus Mongo orders for entry date/price and the runId -> upside join — and records why, so
 * a single failing source never blanks the whole table.
 */
export const composePositionRows = async (
    cfg: AppConfig,
    deps: Partial<PositionsAdapterDeps> = {},
): Promise<ComposePositionRowsResult> => {
    const getPositions = deps.getPositions ?? alpacaGetPositions;
    const getDevicePositions = deps.getDevicePositions ?? ordersApiGetDevicePositions;
    const getOrders = deps.getOrders ?? ordersApiGetOrders;
    const getUpsideByRunIds = deps.getUpsideByRunIds ?? ordersApiGetUpsideByRunIds;
    const getBars = deps.getBars ?? alpacaGetBars;

    const errors: string[] = [];
    const positions = await getPositions(cfg);

    try {
        const device = await getDevicePositions(cfg);
        if (device.error) {
            errors.push(`Device positions service reported an issue: ${device.error}`);
        }
        return { rows: buildRowsFromDevicePositions(positions, device.positions), errors };
    } catch (error) {
        errors.push(
            `Falling back to client-side enrichment: device positions request failed: ${(error as Error).message}`,
        );
    }

    const rows = await composeFromFallbackSources(
        cfg,
        positions,
        { getOrders, getUpsideByRunIds, getBars },
        errors,
    );
    return { rows, errors };
};

/**
 * Preferred path: trading_server has already computed ATR, stop-loss, target, days-to-target
 * and the flags, so each is passed straight through as an override rather than recomputed here.
 * `entryPriceOverride` is deliberately left unset — a device match's `avgEntryPrice` is the same
 * broker fact Alpaca's own REST call already supplies, not a Mongo-corrected buying price.
 */
const buildRowsFromDevicePositions = (
    positions: RawPositionInput[],
    devicePositions: DevicePosition[],
): PositionRow[] => {
    const deviceBySymbol = new Map(devicePositions.map((device) => [device.symbol, device]));
    const enrichBySymbol: Record<string, EnrichmentInput> = {};
    const positionsWithLastUpdated: RawPositionInput[] = [];

    for (const pos of positions) {
        const device = deviceBySymbol.get(pos.symbol);
        enrichBySymbol[pos.symbol] = device ? deviceToEnrichment(device) : {};
        positionsWithLastUpdated.push(withLastUpdated(pos, device));
    }

    return buildPositionRows(positionsWithLastUpdated, enrichBySymbol);
};

const deviceToEnrichment = (device: DevicePosition): EnrichmentInput => ({
    entryDate: device.entryDate,
    entryDateSource: device.entryDateSource,
    daysHeld: device.daysHeld,
    runId: device.runId,

    atr14Override: device.atr14,
    atrPctOverride: device.atrPct,
    prevDayClose: device.prevDayClose,

    stopLevel: device.stopLevel,
    stopLossBreached: device.stopLossBreached,

    target: device.target,
    targetSource: device.targetSource,
    upsidePct: device.upsidePct,
    upsideRemainingPct: device.upsideRemainingPct,
    estimatedDays: device.estimatedDays,
    daysToTarget: device.daysToTarget,
    daysOverdue: device.daysOverdue,

    holdingTimeFlagged: device.holdingTimeFlagged,
    deadTradeFlagged: device.deadTradeFlagged,
});

/** Layers a device match's freshness timestamp onto the Alpaca position; `lastUpdated` is a
 * broker fact on `RawPositionInput` itself, not an `EnrichmentInput` field. */
const withLastUpdated = (
    pos: RawPositionInput,
    device: DevicePosition | undefined,
): RawPositionInput => ({
    ...pos,
    lastUpdated: device?.lastUpdated ?? pos.lastUpdated ?? null,
});

/**
 * The pre-device-endpoint degradation path, unchanged from before this endpoint existed:
 * Alpaca daily bars for client-side ATR, plus Mongo orders for entry date/price and the
 * runId -> upside join. A failure in the upside lookup or a single symbol's bars only drops
 * that field to null and records why — it never fails the whole load.
 */
const composeFromFallbackSources = async (
    cfg: AppConfig,
    positions: RawPositionInput[],
    deps: {
        getOrders: typeof ordersApiGetOrders;
        getUpsideByRunIds: typeof ordersApiGetUpsideByRunIds;
        getBars: typeof alpacaGetBars;
    },
    errors: string[],
): Promise<PositionRow[]> => {
    const orders = await deps.getOrders(cfg);
    const entryInfoBySymbol = buildEntryInfoBySymbol(orders);

    const runIds = Array.from(
        new Set(positions.flatMap((pos) => entryInfoBySymbol[pos.symbol]?.runIds ?? [])),
    );

    let upsideMap: Record<string, number> = {};
    try {
        upsideMap = await deps.getUpsideByRunIds(cfg, runIds);
    } catch (error) {
        errors.push(`Failed to load upside analysis: ${(error as Error).message}`);
    }

    const barsBySymbol: Record<string, RawBar[]> = {};
    await Promise.all(
        positions.map(async (pos) => {
            try {
                barsBySymbol[pos.symbol] = await deps.getBars(cfg, pos.symbol);
            } catch (error) {
                errors.push(`Failed to load bars for ${pos.symbol}: ${(error as Error).message}`);
            }
        }),
    );

    const enrichBySymbol: Record<string, EnrichmentInput> = {};
    for (const pos of positions) {
        const info = entryInfoBySymbol[pos.symbol];
        enrichBySymbol[pos.symbol] = {
            entryDate: info?.entryDate ?? null,
            entryPriceOverride: info?.buyingPrice ?? null,
            bars: barsBySymbol[pos.symbol] ?? null,
            upsidePct: upsideMap[pos.symbol] ?? null,
        };
    }

    return buildPositionRows(positions, enrichBySymbol);
};

const buildEntryInfoBySymbol = (orders: OrderDoc[]): Record<string, EntryInfo> => {
    const ordersBySymbol = new Map<string, OrderDoc[]>();
    for (const order of orders) {
        const list = ordersBySymbol.get(order.symbol) ?? [];
        list.push(order);
        ordersBySymbol.set(order.symbol, list);
    }

    const entryInfoBySymbol: Record<string, EntryInfo> = {};
    for (const [symbol, symbolOrders] of ordersBySymbol) {
        const chosen = pickOrderForSymbol(symbolOrders);
        entryInfoBySymbol[symbol] = {
            entryDate: chosen.orderDate,
            buyingPrice: chosen.buyingPrice,
            runIds: chosen.runIds,
        };
    }
    return entryInfoBySymbol;
};

// Picks the best order for a symbol: prefer an open order, then the one with
// the most recent orderDate among the candidates.
const pickOrderForSymbol = (orders: OrderDoc[]): OrderDoc => {
    const openOrders = orders.filter((order) => order.status === 'open');
    const candidates = openOrders.length > 0 ? openOrders : orders;

    return candidates.reduce((latest, current) =>
        new Date(current.orderDate).getTime() > new Date(latest.orderDate).getTime()
            ? current
            : latest,
    );
};

interface EntryInfo {
    entryDate: string;
    buyingPrice: number;
    runIds: string[];
}
